import * as Analytics from "../analytics";
import {
    ApiQuery,
    BasicFilter,
    CompositeFilter,
    Filter,
    FilterMetadata,
    FilterTypes,
    FilterOperators,
} from "~/modules/filters/types";
import { SEARCH_FILTER_METADATA } from "../consts";
import { Button } from "~/components/ui/button";
import { StringMultiSelect } from "./inputs/StringMultiSelect";
import { sortFilters } from "./sortFilters";
import { SearchValueInput } from "./inputs/SearchValueInput";
import { cn } from "~/lib/utils";
import { Toggle } from "~/components/Toggle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { Tooltip } from "~/components/Tooltip";
import { useUser } from "~/modules/user/hooks/useUser";
import { SelectLoginGate } from "~/components/SelectInput/SelectLoginGate";

// // // //

// Add TypeScript interface for component props
interface InlineFilterFormProps {
    filter: BasicFilter;
    filterMetadata: FilterMetadata[];
    filterIndex: number;
    filterCount: number;
    isLoggedIn: boolean;
    loading?: boolean;
    onChange: (updatedFilter: BasicFilter) => void;
}

function InlineFilterForm(props: InlineFilterFormProps) {
    const {
        filter,
        filterMetadata,
        onChange,
        filterIndex,
        filterCount,
        isLoggedIn,
        loading,
    } = props;
    {
    }
    const selectedFilterMetadata = filterMetadata.find(
        (each) => each.field === filter.field,
    );

    if (!selectedFilterMetadata) {
        return null;
    }

    const isSearchField =
        selectedFilterMetadata.field === SEARCH_FILTER_METADATA.field;

    return (
        <div
            key={selectedFilterMetadata.field}
            className={cn({
                "w-64": isSearchField,
                flex: !isSearchField,
            })}
        >
            {selectedFilterMetadata.type === FilterTypes.StringSingleSelect && (
                <SelectLoginGate
                    isLoggedIn={isLoggedIn}
                    tooltipText="Log in to filter data"
                >
                    <StringMultiSelect
                        value={
                            typeof filter.value === "string"
                                ? []
                                : (filter.value as string[])
                        }
                        disabled={!isLoggedIn || loading}
                        filterMetadata={selectedFilterMetadata}
                        onChange={(updatedValue: Array<string | number>) => {
                            onChange({
                                ...filter,
                                value: updatedValue,
                            });
                        }}
                    />
                </SelectLoginGate>
            )}

            {selectedFilterMetadata.type === FilterTypes.Search && (
                <SelectLoginGate
                    isLoggedIn={isLoggedIn}
                    tooltipText="Log in to search"
                >
                    <SearchValueInput
                        className="h-8.5"
                        disabled={!isLoggedIn || loading}
                        value={
                            typeof filter.value === "string" ? filter.value : ""
                        }
                        filterMeta={selectedFilterMetadata}
                        onChange={(updatedValue) => {
                            Analytics.trackInlineSearch({
                                value: updatedValue,
                            });
                            onChange({
                                ...filter,
                                value: updatedValue,
                            });
                        }}
                    />
                </SelectLoginGate>
            )}

            {selectedFilterMetadata.type === FilterTypes.Boolean && (
                <div className="flex flex-col justify-between">
                    {/* ENHANCEMENT - break this header + tooltip out into a dedicated component */}
                    <div className="flex items-center gap-2">
                        <p className="label-03 leading-normal">
                            {selectedFilterMetadata.content.label}
                        </p>

                        {selectedFilterMetadata.content.description && (
                            <Tooltip
                                placement="top-start"
                                tooltipContent={
                                    <p className="py-2 leading-normal max-w-160 px-2">
                                        {
                                            selectedFilterMetadata.content
                                                .description
                                        }
                                    </p>
                                }
                            >
                                {({ setTriggerRef }) => (
                                    <span ref={setTriggerRef}>
                                        <FontAwesomeIcon
                                            icon={faQuestionCircle}
                                            className="text-slate-600 hover:text-slate-700 transition-colors h-4 cursor-help"
                                        />
                                    </span>
                                )}
                            </Tooltip>
                        )}
                    </div>

                    <div className="my-2.5">
                        <Toggle
                            checked={
                                typeof filter.value === "boolean"
                                    ? filter.value
                                    : false
                            }
                            onChange={(updatedValue) => {
                                // TODO - add analytics
                                // Analytics.trackInlineMultiSelectSelectAll({ value: updatedValue });
                                onChange({
                                    ...filter,
                                    value: updatedValue,
                                });
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

// // // //

// Add helper function to check if filter value is empty
const isFilterValueEmpty = (value: unknown): boolean => {
    return (
        value === "" ||
        value === null ||
        (Array.isArray(value) && value.length === 0)
    );
};

function InlineFilterList(props: {
    filter: Filter;
    filterMetadata: FilterMetadata[];
    onChange: (updatedFilter: Filter) => void;
    isLoggedIn: boolean;
    loading?: boolean;
}) {
    const { filter, filterMetadata, isLoggedIn, loading } = props;

    // Filter only the metadata items that should be displayed inline
    const inlineFilterMetadata = filterMetadata.filter(
        (each) => each.displayInline,
    );

    return (
        <>
            {inlineFilterMetadata.map((metadata, filterIndex) => {
                // Find existing filter for this metadata field
                const existingFilter = filter.filters?.find(
                    (f) => f.field === metadata.field,
                );

                // If no existing filter found, create default one
                const currentFilter = existingFilter || {
                    field: metadata.field,
                    operator:
                        metadata.type === FilterTypes.Search
                            ? FilterOperators.Like
                            : metadata.allowedOperators.includes(
                                    FilterOperators.Equals,
                                )
                              ? FilterOperators.Equals
                              : metadata.defaultOperator,
                    value:
                        metadata.type === FilterTypes.Boolean
                            ? false
                            : metadata.type === FilterTypes.StringSingleSelect
                              ? []
                              : "",
                };

                return (
                    <InlineFilterForm
                        key={metadata.field}
                        filter={currentFilter as BasicFilter}
                        filterMetadata={filterMetadata}
                        filterIndex={filterIndex}
                        filterCount={inlineFilterMetadata.length}
                        isLoggedIn={isLoggedIn}
                        loading={loading}
                        onChange={(updatedFilter: BasicFilter) => {
                            const updatedFilters = filter.filters || [];
                            const existingIndex = updatedFilters.findIndex(
                                (f) => f.field === metadata.field,
                            );

                            // Use helper function for cleaner empty check
                            if (isFilterValueEmpty(updatedFilter.value)) {
                                if (existingIndex >= 0) {
                                    updatedFilters.splice(existingIndex, 1);
                                }
                            } else {
                                // Use ternary for cleaner logic
                                existingIndex >= 0
                                    ? (updatedFilters[existingIndex] =
                                          updatedFilter)
                                    : updatedFilters.push(updatedFilter);
                            }

                            props.onChange({
                                ...filter,
                                filters: sortFilters(updatedFilters),
                            } as Filter);
                        }}
                    />
                );
            })}
        </>
    );
}

// // // //

// Add interface for main component props
interface InlineFiltersProps {
    query: ApiQuery;
    onChange: (updatedApiQuery: ApiQuery) => void;
    filterMetadata: FilterMetadata[];
    enableClearFilters?: boolean;
    onClear?: () => void;
    loading?: boolean;
}

export function InlineFilters(props: InlineFiltersProps) {
    const {
        query,
        onChange,
        filterMetadata,
        onClear,
        enableClearFilters = true,
        loading = false,
    } = props;

    const { isLoggedIn } = useUser();

    const defaultFilter: CompositeFilter = {
        operator: FilterOperators.And,
        filters: [],
    };

    const filter = query.filter || defaultFilter;

    return (
        <div className="flex w-full flex-col">
            <div className="flex gap-4 flex-wrap items-center">
                <InlineFilterList
                    filter={filter}
                    filterMetadata={filterMetadata}
                    isLoggedIn={isLoggedIn}
                    loading={loading}
                    onChange={(updatedFilter: Filter) => {
                        onChange({
                            ...query,
                            filter: updatedFilter,
                        });
                    }}
                />

                {/* Render the `Clear Filters` button only when enabled (enabled by default) */}
                {enableClearFilters && (
                    <Button
                        size="sm"
                        disabled={!isLoggedIn}
                        variant="link"
                        className="px-0"
                        onClick={() => {
                            Analytics.trackInlineClearFilters();
                            onChange({
                                ...query,
                                filter: undefined,
                            });
                            if (onClear) onClear();
                        }}
                    >
                        Clear Filters
                    </Button>
                )}
            </div>
        </div>
    );
}
